import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTimes, faCommentDots} from "@fortawesome/free-solid-svg-icons";
import * as React from "react";
import dayjs from "dayjs";
import {useBoundStore} from "../../../Store/store";
import {useTranslation} from "react-i18next";

export const RemarksDialog = (props) => {
    const {
        open,
        onClose,
        remarks = []
    } = props;

    const {t} = useTranslation();
    const user = useBoundStore((state) => state.user);

    const isCurrentUser = (remark) => {
        return remark.user_id === user?.id;
    };

    const getUserName = (remark) => {
        if (isCurrentUser(remark)) {
            return `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || t('performance_reports.you');
        }
        return remark.user_name || t('performance_reports.other_user');
    };

    return (
        <Dialog className="relative z-50" open={open} onClose={onClose}>
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0"
            />

            <div className="fixed inset-0 overflow-y-scroll">
                <div className="flex min-h-full items-center justify-center p-4">
                    <DialogPanel
                        transition
                        className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all duration-300 ease-in-out data-[closed]:scale-95 data-[closed]:opacity-0"
                    >
                        <div className="bg-white px-6 py-6">
                            <div className="flex items-start justify-between mb-6">
                                <DialogTitle className="text-lg font-semibold leading-6 text-gray-900 flex items-center">
                                    <FontAwesomeIcon icon={faCommentDots} className="mr-2 text-primary" />
                                    {t('general.fields.remarks')}
                                </DialogTitle>
                                <button
                                    type="button"
                                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                                    onClick={onClose}
                                >
                                    <span className="sr-only">{t('general.actions.close')}</span>
                                    <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
                                </button>
                            </div>

                            {remarks.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <FontAwesomeIcon icon={faCommentDots} className="h-12 w-12 mb-3 opacity-30" />
                                    <p>{t('performance_reports.no_remarks')}</p>
                                </div>
                            ) : (
                                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                                    {remarks.map((remark) => {
                                        const timestamp = remark.created_at?.timestamp
                                            ? dayjs.unix(remark.created_at.timestamp).format('DD/MM/YYYY - HH:mm')
                                            : remark.created_at?.formatted
                                            ? dayjs(remark.created_at.formatted, 'DD/MM/YYYY HH:mm:ss').format('DD/MM/YYYY - HH:mm')
                                            : '';

                                        return (
                                            <div
                                                key={remark.id}
                                                className={`flex ${isCurrentUser(remark) ? 'justify-end' : 'justify-start'}`}
                                            >
                                                <div
                                                    className={`max-w-[75%] rounded-lg px-4 py-2 ${
                                                        isCurrentUser(remark)
                                                            ? 'bg-blue-600 text-white'
                                                            : 'bg-gray-200 text-gray-900'
                                                    }`}
                                                >
                                                    <p
                                                        className={`text-xs font-semibold mb-1 ${
                                                            isCurrentUser(remark)
                                                                ? 'text-blue-100'
                                                                : 'text-gray-700'
                                                        }`}
                                                    >
                                                        {getUserName(remark)}
                                                    </p>
                                                    <p className="text-sm whitespace-pre-wrap break-words">
                                                        {remark.content}
                                                    </p>
                                                    {timestamp && (
                                                        <p
                                                            className={`text-xs mt-1.5 ${
                                                                isCurrentUser(remark)
                                                                    ? 'text-blue-200'
                                                                    : 'text-gray-600'
                                                            }`}
                                                        >
                                                            {timestamp}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
};
